import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { RIDDLE_STATUSES } from '../config';
import { useAuth } from '../context/AuthContext';
import { loadReferences } from '../lib/data';
import { errorMessage } from '../lib/errors';
import { sha256Hex, stableStringify } from '../lib/hash';
import { supabase } from '../lib/supabase';
import { normalizeAnswer } from '../lib/text';
import type { Category, ContentPack, Language, Riddle, RiddleStatus } from '../lib/types';

interface FormState {
  id: string;
  content_pack_id: string;
  category_id: string;
  language_code: string;
  title_internal: string;
  content_text: string;
  answer_display: string;
  aliases: string;
  is_daily_eligible: boolean;
  manual_excluded_words: string;
  manual_equivalence_groups: string;
  difficulty: number;
  status: RiddleStatus;
  game_data: string;
  source_payload: string;
}

const EMPTY_FORM: FormState = {
  id: '',
  content_pack_id: '',
  category_id: '',
  language_code: 'fr',
  title_internal: '',
  content_text: '',
  answer_display: '',
  aliases: '',
  is_daily_eligible: true,
  manual_excluded_words: '[]',
  manual_equivalence_groups: '[]',
  difficulty: 2,
  status: 'draft',
  game_data: '{}',
  source_payload: '{}',
};

export function RiddleFormPage() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const refs = await loadReferences();
        setLanguages(refs.languages);
        setCategories(refs.categories);
        setPacks(refs.packs);

        if (!id) {
          setForm((current) => ({
            ...current,
            content_pack_id: refs.packs[0]?.id ?? '',
            category_id: refs.categories[0]?.id ?? '',
            language_code: refs.languages[0]?.code ?? 'fr',
          }));
          return;
        }

        const [riddleResult, aliasesResult] = await Promise.all([
          supabase.from('riddles').select('*').eq('id', id).single(),
          supabase.from('answer_aliases').select('*').eq('riddle_id', id).order('id'),
        ]);
        if (riddleResult.error) throw riddleResult.error;
        if (aliasesResult.error) throw aliasesResult.error;
        const riddle = riddleResult.data as Riddle;
        setForm({
          id: riddle.id,
          content_pack_id: riddle.content_pack_id,
          category_id: riddle.category_id,
          language_code: riddle.language_code,
          title_internal: riddle.title_internal,
          content_text: riddle.content_text,
          answer_display: riddle.answer_display,
          aliases: (aliasesResult.data ?? []).map((item) => `${item.display}\t${item.alias_type ?? 'accepted'}\t${item.is_full_answer ?? true}`).join('\n'),
          is_daily_eligible: riddle.is_daily_eligible ?? true,
          manual_excluded_words: JSON.stringify(riddle.manual_excluded_words ?? [], null, 2),
          manual_equivalence_groups: JSON.stringify(riddle.manual_equivalence_groups ?? [], null, 2),
          difficulty: riddle.difficulty,
          status: riddle.status,
          game_data: JSON.stringify(riddle.game_data ?? {}, null, 2),
          source_payload: JSON.stringify(riddle.source_payload ?? {}, null, 2),
        });
      } catch (caught) {
        setError(errorMessage(caught));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [id]);

  const filteredPacks = useMemo(
    () => packs.filter((pack) => pack.language_code === form.language_code),
    [form.language_code, packs],
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const gameData = JSON.parse(form.game_data || '{}') as Record<string, unknown>;
      const sourcePayload = JSON.parse(form.source_payload || '{}') as Record<string, unknown>;
      const manualExcludedWords = JSON.parse(form.manual_excluded_words || '[]') as unknown;
      const manualEquivalenceGroups = JSON.parse(form.manual_equivalence_groups || '[]') as unknown;
      if (!Array.isArray(manualExcludedWords) || !Array.isArray(manualEquivalenceGroups)) {
        throw new Error('Les champs manuels doivent être des tableaux JSON.');
      }
      const aliases = Array.from(
        new Map(
          form.aliases
            .split(/\r?\n/)
            .map((line) => line.split('\t'))
            .map(([display = '', type = 'accepted', full = 'true']) => ({
              display: display.trim(),
              type: type.trim() || 'accepted',
              is_full_answer: full.trim().toLowerCase() !== 'false',
            }))
            .filter((alias) => Boolean(alias.display))
            .map((alias) => [normalizeAnswer(alias.display), alias]),
        ).values(),
      );

      if (!/^[a-z0-9_]+$/.test(form.id)) {
        throw new Error('L’identifiant doit contenir uniquement des minuscules, chiffres et underscores.');
      }
      if (!form.content_pack_id || !form.category_id || !form.language_code) {
        throw new Error('La langue, la catégorie et le pack sont obligatoires.');
      }

      const hashPayload = {
        content_pack_id: form.content_pack_id,
        category_id: form.category_id,
        language_code: form.language_code,
        title_internal: form.title_internal,
        content_text: form.content_text,
        answer_display: form.answer_display,
        aliases,
        is_daily_eligible: form.is_daily_eligible,
        manual_excluded_words: manualExcludedWords,
        manual_equivalence_groups: manualEquivalenceGroups,
        difficulty: form.difficulty,
        game_data: gameData,
      };
      const contentHash = await sha256Hex(stableStringify(hashPayload));
      const riddlePayload = {
        id: form.id,
        content_pack_id: form.content_pack_id,
        category_id: form.category_id,
        language_code: form.language_code,
        title_internal: form.title_internal.trim(),
        content_text: form.content_text.trim(),
        answer_display: form.answer_display.trim(),
        is_daily_eligible: form.is_daily_eligible,
        manual_excluded_words: manualExcludedWords,
        manual_equivalence_groups: manualEquivalenceGroups,
        difficulty: form.difficulty,
        status: form.status,
        game_data: gameData,
        source_payload: sourcePayload,
        content_hash: contentHash,
        updated_by: user?.id ?? null,
        ...(isEditing ? {} : { created_by: user?.id ?? null }),
      };

      const { error: saveError } = isEditing
        ? await supabase.from('riddles').update(riddlePayload).eq('id', id)
        : await supabase.from('riddles').insert(riddlePayload);
      if (saveError) throw saveError;

      const { error: deleteAliasesError } = await supabase
        .from('answer_aliases')
        .delete()
        .eq('riddle_id', form.id);
      if (deleteAliasesError) throw deleteAliasesError;

      if (aliases.length > 0) {
        const { error: aliasError } = await supabase.from('answer_aliases').insert(
          aliases.map((alias) => ({
            riddle_id: form.id,
            display: alias.display,
            normalized_display: normalizeAnswer(alias.display),
            alias_type: alias.type,
            is_full_answer: alias.is_full_answer,
          })),
        );
        if (aliasError) throw aliasError;
      }

      if (!isEditing) {
        navigate(`/riddles/${encodeURIComponent(form.id)}`, { replace: true });
      }
      setSuccess('Énigme enregistrée.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="panel">Chargement de l’énigme…</div>;

  return (
    <form className="page-stack" onSubmit={save}>
      <header className="page-header">
        <div>
          <p className="eyebrow">{isEditing ? 'Modification' : 'Création'}</p>
          <h1>{isEditing ? form.title_internal || form.id : 'Nouvelle énigme'}</h1>
          <p>Les champs JSON avancés sont conservés dans le pack SQLite publié.</p>
        </div>
        <div className="header-actions">
          <Link className="button secondary" to="/riddles">Retour</Link>
          <button className="button primary" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      <section className="panel form-grid">
        <label>
          Identifiant
          <input
            required
            readOnly={isEditing}
            value={form.id}
            placeholder="fr_movies_series_0001"
            onChange={(event) => update('id', event.target.value.trim().toLowerCase())}
          />
        </label>
        <label>
          Langue
          <select
            required
            value={form.language_code}
            onChange={(event) => {
              update('language_code', event.target.value);
              const firstPack = packs.find((pack) => pack.language_code === event.target.value);
              if (firstPack) update('content_pack_id', firstPack.id);
            }}
          >
            {languages.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}
          </select>
        </label>
        <label>
          Catégorie
          <select required value={form.category_id} onChange={(event) => update('category_id', event.target.value)}>
            {categories.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <label>
          Pack
          <select required value={form.content_pack_id} onChange={(event) => update('content_pack_id', event.target.value)}>
            <option value="">Sélectionner…</option>
            {filteredPacks.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label className="span-2">
          Titre interne
          <input required value={form.title_internal} onChange={(event) => update('title_internal', event.target.value)} />
        </label>
        <label className="span-2">
          Texte de l’énigme
          <textarea required rows={12} value={form.content_text} onChange={(event) => update('content_text', event.target.value)} />
        </label>
        <label>
          Réponse principale
          <input required value={form.answer_display} onChange={(event) => update('answer_display', event.target.value)} />
        </label>
        <label>
          Difficulté
          <select value={form.difficulty} onChange={(event) => update('difficulty', Number(event.target.value))}>
            {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value}/5</option>)}
          </select>
        </label>
        <label>
          Statut
          <select value={form.status} onChange={(event) => update('status', event.target.value as RiddleStatus)}>
            {RIDDLE_STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label className="span-2">
          Alias — affichage, type, réponse complète (true/false), séparés par tabulation
          <textarea rows={6} value={form.aliases} onChange={(event) => update('aliases', event.target.value)} />
        </label>
        <label className="checkbox-label span-2">
          <input type="checkbox" checked={form.is_daily_eligible} onChange={(event) => update('is_daily_eligible', event.target.checked)} />
          Éligible à l’énigme quotidienne
        </label>
      </section>

      <details className="panel advanced-panel">
        <summary>Données avancées</summary>
        <div className="form-grid top-gap">
          <label className="span-2">
            Mots exclus manuellement (tableau JSON)
            <textarea className="code-editor" rows={8} value={form.manual_excluded_words} onChange={(event) => update('manual_excluded_words', event.target.value)} />
          </label>
          <label className="span-2">
            Groupes d’équivalence manuels (tableau JSON)
            <textarea className="code-editor" rows={10} value={form.manual_equivalence_groups} onChange={(event) => update('manual_equivalence_groups', event.target.value)} />
          </label>
          <label className="span-2">
            game_data JSON
            <textarea className="code-editor" rows={14} value={form.game_data} onChange={(event) => update('game_data', event.target.value)} />
          </label>
          <label className="span-2">
            source_payload JSON
            <textarea className="code-editor" rows={14} value={form.source_payload} onChange={(event) => update('source_payload', event.target.value)} />
          </label>
        </div>
      </details>
    </form>
  );
}
